import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { useChainContext } from '../../../context/context';
import { getAppSocket } from '@/utils/socket';
import { getStoredIdentity, getStoredUser } from '@/utils/services/session';
import { canAccess } from '@/utils/services/authorization';
import SidebarLink from './SidebarLink';
import SidebarCategory from './SidebarCategory';

const navigation = [
  { section: 'Overview', items: [
    { title: 'Dashboard', route: '/dashboard', icon: 'home' },
    { title: 'User dashboard', route: '/appdashboard', icon: 'users' },
    { title: 'Target financial', route: '/target-financial', icon: 'chart-bars' },
  ] },
  { section: 'Management', items: [
    { title: 'Users', route: '/users', icon: 'user' },
    {
      title: 'Administrators',
      route: '/administrators',
      icon: 'lock',
      allowedRoles: ['admin', 'super_admin', 'superadmin'],
    },
    { title: 'Contracts', route: '/contracts', icon: 'file-empty' },
    { title: 'Services', route: '/add-services', icon: 'cog' },
    { title: 'Games', route: '/games', icon: 'star' },
  ] },
  { section: 'Commerce', items: [
    { title: 'Marketplace', icon: 'store', children: [
      { title: 'Products', route: '/all-products' }, { title: 'Stores', route: '/userstores' },
      { title: 'Orders', route: '/userorders' }, { title: 'Categories', route: '/categories' },
      { title: 'Approvers', route: '/approverslist' },
    ] },
    { title: 'OTC marketplace', icon: 'layers', children: [
      { title: 'OTC requests', route: '/otcmarketplace' },
      { title: 'Marketplace lists', route: '/otcmarketplacelists' },
      { title: 'Access requests', route: '/accessrequest' },
    ] },
    { title: 'Projects', icon: 'briefcase', children: [{ title: 'Project requests', route: '/project-requets' }] },
    { title: 'Sub accounts', icon: 'users', children: [
      { title: 'Account requests', route: '/allsubaccounts' }, { title: 'Sub accounts', route: '/subaccounts' },
    ] },
  ] },
  { section: 'Operations', items: [
    { title: 'Web app', icon: 'screen', children: [
      { title: 'Blockchain operations', route: '/blockchain-operations' },
      { title: 'Socket & API monitoring', route: '/service-monitoring' },
      { title: 'CTY market maker', route: '/market-maker' },
      { title: 'Withdrawals', route: '/withdraw-report' }, { title: 'Internal transfers', route: '/internal-transfer' },
      { title: 'Wallet activities', route: '/wallet-activities' }, { title: 'Migration report', route: '/migration-report' },
      { title: 'Custodiy wallet', route: '/CustodiyWallet' }, { title: 'Fees', route: '/fee' },
      { title: 'Tokens', route: '/tokens' }, { title: 'Coin balances', route: '/coinbalance' },
      { title: 'Transactions', route: '/transactions' },
    ] },
    { title: 'Content', icon: 'smartphone', children: [
      { title: 'Mobile content', route: '/mobile-content' }, { title: 'App downloads', route: '/app-downloads' }, { title: 'News', route: '/news' },
      { title: 'Partners', route: '/partners' }, { title: 'Service catalogue', route: '/services' },
      { title: 'SEO settings', route: '/seo-settings' },
      { title: 'Privacy notice', route: '/privacy-notice' }, { title: 'Cookie policy', route: '/cookie-policy' },
    ] },
  ] },
  { section: 'Communication', items: [
    { title: 'Notify users', route: '/notify-users', icon: 'alarm' },
    { title: 'Support tickets', route: '/Support', icon: 'bubble' }, { title: 'Chat', route: '/Chat', icon: 'envelope' },
  ] },
];

const SidebarContent = ({ onClick, collapse }) => {
  const { setSocket } = useChainContext();
  const socket = getAppSocket();
  const userId = getStoredIdentity()?.user_id;
  const session = getStoredUser();
  const isVisible = item => canAccess({
    session,
    allowedRoles: item.allowedRoles,
    requiredPermissions: item.requiredPermissions,
  });

  useEffect(() => {
    if (!userId) return undefined;
    setSocket(socket);
    const handleConnect = () => socket.emit('connected', { subject: 'admin' });
    const handleMessage = message => toast.info(`${message?.user?.name || 'A user'} sent you a message`);
    socket.on('connect', handleConnect);
    socket.on('message', handleMessage);
    if (socket.connected) handleConnect();
    return () => { socket.off('connect', handleConnect); socket.off('message', handleMessage); };
  }, [setSocket, socket, userId]);

  return (
    <SidebarContentWrap>
      {!collapse && <Workspace><WorkspaceMark>C</WorkspaceMark><div><strong>Custodiy</strong><span>Admin workspace</span></div></Workspace>}
      <SidebarBlock>
        {navigation.map(group => (
          <NavigationGroup key={group.section}>
            {!collapse && <SectionLabel>{group.section}</SectionLabel>}
            {group.items.filter(isVisible).map(item => item.children ? (
              <SidebarCategory key={item.title} title={item.title} icon={item.icon} collapse={collapse}>
                {!collapse && item.children.map(child => <SidebarLink key={child.route} {...child} onClick={onClick} />)}
              </SidebarCategory>
            ) : <SidebarLink key={item.route} {...item} onClick={onClick} />)}
          </NavigationGroup>
        ))}
      </SidebarBlock>
    </SidebarContentWrap>
  );
};

SidebarContent.propTypes = { onClick: PropTypes.func.isRequired, collapse: PropTypes.bool.isRequired };
export default SidebarContent;

const SidebarContentWrap = styled.div`padding: 14px 0 32px; color: #cbd5e1;`;
const SidebarBlock = styled.ul`margin: 0; padding: 0; list-style: none;`;
const NavigationGroup = styled.div`margin-bottom: 18px; & > li { list-style: none; }`;
const SectionLabel = styled.div`padding: 0 26px 8px; color: #64748b; font-size: 10px; font-weight: 800; letter-spacing: .14em; text-transform: uppercase;`;
const Workspace = styled.div`
  display: flex; align-items: center; gap: 12px; margin: 0 14px 22px; padding: 13px;
  border: 1px solid rgba(148,163,184,.12); border-radius: 13px; background: rgba(255,255,255,.035);
  strong, span { display: block; } strong { color: #f8fafc; font-size: 13px; } span { color: #64748b; font-size: 10px; }
`;
const WorkspaceMark = styled.span`
  display: grid !important; width: 34px; height: 34px; place-items: center; color: #fff !important;
  border-radius: 10px; background: linear-gradient(135deg,#8b5cf6,#4f46e5); font-weight: 800;
`;
